import React from 'react';
import { format } from 'date-fns';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  Radio,
  Stack,
  Typography,
} from '@mui/material';
import { EmptyState, LicensePlate, StatusChip } from '../../../components/ui';
import { Appointment } from '../../../types';
import { shopWallClockDate } from '../../../utils/shopTime';
import { bayLabel, vehicleOf } from './helpers';

interface AssignBayDialogProps {
  /** The empty bay being filled; null keeps the dialog closed. */
  bay: number | null;
  candidates: Appointment[];
  timeZone?: string;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onAssign: (appointment: Appointment, bay: number) => void;
}

/**
 * Drops one of today's jobs into an empty bay. Assigning also starts the job
 * (in progress), since a car in a bay is a car being worked on.
 */
const AssignBayDialog: React.FC<AssignBayDialogProps> = ({
  bay,
  candidates,
  timeZone,
  saving,
  error,
  onClose,
  onAssign,
}) => {
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (bay !== null) setSelectedId(candidates.length === 1 ? candidates[0]!.id : null);
    // Reset only when a different bay is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bay]);

  const selected = candidates.find((job) => job.id === selectedId) ?? null;

  return (
    <Dialog open={bay !== null} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{bay !== null ? `Assign a job to ${bayLabel(bay)}` : 'Assign a job'}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {candidates.length === 0 ? (
          <EmptyState
            dense
            title="No jobs waiting for a bay"
            description="Today's bookings that are not finished and not already in a bay show up here."
          />
        ) : (
          <List disablePadding aria-label="Today's jobs">
            {candidates.map((job) => (
              <ListItemButton
                key={job.id}
                selected={job.id === selectedId}
                onClick={() => setSelectedId(job.id)}
                sx={{ borderRadius: 1, mb: 0.5, gap: 1.5, alignItems: 'flex-start' }}
              >
                <Radio
                  checked={job.id === selectedId}
                  tabIndex={-1}
                  size="small"
                  sx={{ p: 0.5, mt: -0.25 }}
                  slotProps={{ input: { 'aria-label': `${vehicleOf(job)}, ${job.customerName}` } }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }} noWrap>
                      {vehicleOf(job)}
                    </Typography>
                    {job.licensePlate && <LicensePlate plate={job.licensePlate} />}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" component="div" noWrap>
                    {job.appointmentDateTime
                      ? `${format(shopWallClockDate(job.appointmentDateTime, timeZone), 'h:mm a')} · `
                      : ''}
                    {job.customerName} · {job.serviceDescription}
                  </Typography>
                </Box>
                <StatusChip status={job.status} />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!selected || bay === null || saving}
          onClick={() => selected && bay !== null && onAssign(selected, bay)}
        >
          {saving ? 'Assigning…' : 'Put in bay'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignBayDialog;
